'use client'

import { useComponentTranslation } from '@tebuto/functions/translation.components'
import { createContext, type JSX, useCallback, useEffect, useState } from 'react'
import useLeaveConfirmation from './useLeaveConfirmation'
import { triggerBeforeRouteChangeEvent, triggerRouteChangeConfirmationEvent } from './useRouteChangeEvents'

export type LeaveBehavior = 'confirm' | 'autoSave'

export interface LeavePageGuardRegistration {
    name: string
    save: () => Promise<void>
    discard: () => void
    leaveBehavior?: LeaveBehavior
    onAutoSaveSuccess?: () => void
}

export type SaveGuardContext = {
    registerLeavePageGuard: (config: LeavePageGuardRegistration) => void
    unregisterLeavePageGuard: (name: string) => void
    updateDirtyState: (name: string, isDirty: boolean) => void
    navigate: (target: string, action: () => void) => void
}

export const LeavePageGuardContext = createContext<SaveGuardContext>({} as SaveGuardContext)

interface LeavePageGuardProps {
    children: React.ReactNode | React.ReactNode[]
}

export default function LeavePageGuard({ children }: LeavePageGuardProps): JSX.Element {
    const { t } = useComponentTranslation()
    const [preventRouteChange, setPreventRouteChange] = useState(false)
    const [dirtyStates, setDirtyStates] = useState<{ [key: string]: boolean }>({})
    const [saveCallbacks, setSaveCallbacks] = useState<{ [key: string]: () => Promise<void> }>({})
    const [discardCallbacks, setDiscardCallbacks] = useState<{ [key: string]: () => void }>({})
    const [leaveBehaviors, setLeaveBehaviors] = useState<{ [key: string]: LeaveBehavior }>({})
    const [onAutoSaveSuccessCallbacks, setOnAutoSaveSuccessCallbacks] = useState<{ [key: string]: () => void }>({})

    const saveChanges = useCallback(async () => {
        for (const [name, isDirty] of Object.entries(dirtyStates)) {
            if (isDirty) {
                await saveCallbacks[name]()
                setDirtyStates((states) => ({ ...states, [name]: false }))
            }
        }
    }, [dirtyStates, saveCallbacks])

    const discardChanges = useCallback(() => {
        for (const [name, isDirty] of Object.entries(dirtyStates)) {
            if (isDirty) {
                discardCallbacks[name]()
                setDirtyStates((states) => ({ ...states, [name]: false }))
            }
        }
    }, [dirtyStates, discardCallbacks])

    const tryLeaveAsync = useCallback(async () => {
        const dirtyEntries = Object.entries(dirtyStates).filter(([, isDirty]) => isDirty)
        if (dirtyEntries.length === 0) {
            return { kind: 'proceed' as const }
        }

        const allAutoSave = dirtyEntries.every(([name]) => leaveBehaviors[name] === 'autoSave')
        if (!allAutoSave) {
            return { kind: 'modal' as const }
        }

        try {
            for (const [name] of dirtyEntries) {
                await saveCallbacks[name]()
                setDirtyStates((states) => ({ ...states, [name]: false }))
                onAutoSaveSuccessCallbacks[name]?.()
            }
            return { kind: 'proceed' as const }
        } catch (error) {
            const message = error instanceof Error ? error.message : String(error)
            return {
                kind: 'modal' as const,
                autoSaveErrorMessage: t('clientsPage.documentation.autoSaveFailed', { message })
            }
        }
    }, [dirtyStates, leaveBehaviors, onAutoSaveSuccessCallbacks, saveCallbacks, t])

    const { confirmationDialog, handleManualRouteChange } = useLeaveConfirmation(
        preventRouteChange,
        Object.entries(dirtyStates)
            .filter(([_, isDirty]) => isDirty)
            .map(([name]) => name),
        saveChanges,
        discardChanges,
        tryLeaveAsync
    )

    useEffect(() => {
        const anyStateIsDirty = Object.values(dirtyStates).some((x) => x)
        setPreventRouteChange(anyStateIsDirty)
    }, [dirtyStates])

    const registerLeavePageGuard = useCallback((config: LeavePageGuardRegistration) => {
        setSaveCallbacks((callbacks) => ({ ...callbacks, [config.name]: config.save }))
        setDiscardCallbacks((callbacks) => ({ ...callbacks, [config.name]: config.discard }))
        setLeaveBehaviors((behaviors) => ({
            ...behaviors,
            [config.name]: config.leaveBehavior ?? 'confirm'
        }))
        setOnAutoSaveSuccessCallbacks((cbs) => {
            if (config.onAutoSaveSuccess) {
                return { ...cbs, [config.name]: config.onAutoSaveSuccess }
            }
            const { [config.name]: _, ...rest } = cbs
            return rest
        })
    }, [])

    const unregisterLeavePageGuard = useCallback((name: string) => {
        setSaveCallbacks((callbacks) => {
            const { [name]: _, ...rest } = callbacks
            return rest
        })
        setDiscardCallbacks((callbacks) => {
            const { [name]: _, ...rest } = callbacks
            return rest
        })
        setLeaveBehaviors((behaviors) => {
            const { [name]: _, ...rest } = behaviors
            return rest
        })
        setOnAutoSaveSuccessCallbacks((cbs) => {
            const { [name]: _, ...rest } = cbs
            return rest
        })
        setDirtyStates((states) => {
            const { [name]: _, ...rest } = states
            return rest
        })
    }, [])

    const updateDirtyState = useCallback((name: string, isDirty: boolean) => {
        setDirtyStates((states) => ({ ...states, [name]: isDirty }))
    }, [])

    const navigate = useCallback(
        (target: string, action: () => void) => {
            triggerBeforeRouteChangeEvent(target)
            void handleManualRouteChange(() => {
                action()
                triggerRouteChangeConfirmationEvent(target)
            })
        },
        [handleManualRouteChange]
    )

    return (
        <LeavePageGuardContext.Provider
            value={{ registerLeavePageGuard, unregisterLeavePageGuard, updateDirtyState, navigate }}
        >
            {children}
            {confirmationDialog}
        </LeavePageGuardContext.Provider>
    )
}
